import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "./config/API";

function FloorRoomBedSelector() {
  const navigate = useNavigate();
  const location = useLocation();

  const user = JSON.parse(localStorage.getItem("user"));
  const emailId = user?.emailId || "";

  const [floorDropdownOpen, setFloorDropdownOpen] = useState(false);

  // Floor name received from previous page
  const floorName = location.state?.floorName || "";

  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedFloorData, setSelectedFloorData] = useState(null);
  const [popup, setPopup] = useState("");

  // =====================================================
  // POPUP
  // =====================================================

  const showPopup = (msg) => {
    setPopup(msg);

    setTimeout(() => {
      setPopup("");
    }, 2000);
  };

  // =====================================================
  // FETCH ROOMS
  // =====================================================

  const fetchRooms = async (selectedFloorName) => {
    if (!selectedFloorName) {
      setRooms([]);
      return;
    }

    try {
      const loggedUser = JSON.parse(localStorage.getItem("user"));

      const response = await fetch(
        `${API.V1}/floor_rooms/getRoomsByFloor?emailId=${encodeURIComponent(
          emailId,
        )}&floorName=${encodeURIComponent(selectedFloorName)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${loggedUser?.jwtToken}`,
          },
        },
      );

      if (response.status === 401) {
        alert("Session Expired. Please login again.");

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
        return;
      }

      const data = await response.json();

      if (data.statusCode === 200) {
        setRooms(data.data || []);
      } else {
        setRooms([]);
        showPopup(data.message || "Failed to load rooms");
      }
    } catch (error) {
      console.error(error);
      setRooms([]);
      showPopup("Failed to load rooms");
    }
  };

  // =====================================================
  // FETCH FLOORS
  // =====================================================

  useEffect(() => {
    if (!emailId) {
      navigate("/");
      return;
    }

    const fetchFloors = async () => {
      try {
        const loggedUser = JSON.parse(localStorage.getItem("user"));

        const response = await fetch(
          `${API.V1}/floor_rooms/getFloors?emailId=${encodeURIComponent(
            emailId,
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${loggedUser?.jwtToken}`,
            },
          },
        );

        if (response.status === 401) {
          alert("Session Expired. Please login again.");

          localStorage.clear();
          sessionStorage.clear();

          navigate("/");
          return;
        }

        const data = await response.json();

        if (data.statusCode === 200) {
          const floorList = data.data?.existedfloors || [];

          setFloors(floorList);

          // =================================================
          // AUTO SELECT FLOOR
          // =================================================

          if (floorName) {
            const selected = floorList.find((f) => f.floorName === floorName);

            if (selected) {
              setSelectedFloor(selected.floorId);
              setSelectedFloorData(selected);

              fetchRooms(selected.floorName);
            }
          }
        } else {
          showPopup(data.message || "Failed to load floors");
        }
      } catch (error) {
        console.error(error);
        showPopup("Failed to load floors");
      }
    };

    fetchFloors();
  }, [emailId, floorName, navigate]);

  // =====================================================
  // FLOOR CHANGE
  // =====================================================

  const handleFloorChange = (floorId) => {
    const selectedId = Number(floorId);

    setSelectedFloor(selectedId);
    setFloorDropdownOpen(false);

    const floor = floors.find((f) => f.floorId === selectedId);

    if (!floor) {
      setSelectedFloorData(null);
      setRooms([]);
      return;
    }

    setSelectedFloorData(floor);

    fetchRooms(floor.floorName);
  };

  // =====================================================
  // FLOOR BED STATUS
  // =====================================================

  const availableBeds = Number(
    selectedFloorData?.availableBedByFloorRooms || 0,
  );

  const isBedCreationExceeded = availableBeds <= 0;

  // =====================================================
  // CREATE BED
  // =====================================================

  const handleCreateBed = (room) => {
    const createdBeds = Number(room.createdBeds || 0);
    const maxBeds = Number(room.maxBedsCount || 0);

    if (createdBeds >= maxBeds) {
      showPopup("Bed Creation Exceed for this Room");
      return;
    }

    if (availableBeds <= 0) {
      showPopup("Bed Creation Exceed for this Floor");
      return;
    }

    navigate("/createBedForm", {
      state: {
        floorId: room.floorId,
        floorName: room.floorName,
        roomId: room.roomId,
        roomName: room.roomName,
        createdBeds: room.createdBeds,
        maxBedsCount: room.maxBedsCount,
      },
    });
  };

  // =====================================================
  // DASHBOARD
  // =====================================================

  const handleDashboard = () => {
    navigate("/floorRoomsBeds");
  };

  return (
    <div style={styles.container}>
      {/* ===================================================
          POPUP
      =================================================== */}

      {popup && (
        <div style={styles.popup}>
          <span style={styles.popupIcon}>⚠</span>
          {popup}
        </div>
      )}

      <div style={styles.card}>
        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageBadge}>FLOOR MANAGEMENT</div>

            <h1 style={styles.title}>🛏 Bed Management</h1>

            <p style={styles.subtitle}>
              Select a floor and manage room bed configuration
            </p>
          </div>

          <div style={styles.totalFloors}>
            <span style={styles.totalNumber}>{floors.length}</span>

            <span style={styles.totalLabel}>Floors</span>
          </div>
        </div>

        {/* =================================================
            FLOOR SELECTOR
        ================================================= */}

        <div style={styles.section}>
          <div style={styles.sectionLabel}>
            <span>🏢</span>
            <span>Select Floor</span>
          </div>

          <div style={styles.dropdownContainer}>
            <div
              style={{
                ...styles.dropdownHeader,
                ...(floorDropdownOpen ? styles.dropdownHeaderActive : {}),
              }}
              onClick={() => setFloorDropdownOpen(!floorDropdownOpen)}
            >
              {selectedFloorData ? (
                <div style={styles.selectedFloorContent}>
                  <div style={styles.selectedFloorName}>
                    {selectedFloorData.floorName}
                  </div>

                  <span
                    style={
                      availableBeds > 0 ? styles.greenBadge : styles.redBadge
                    }
                  >
                    {availableBeds > 0 ? "● Available" : "● Exceed"}
                  </span>
                </div>
              ) : (
                <span style={styles.placeholder}>Select Floor</span>
              )}

              <span style={styles.arrow}>{floorDropdownOpen ? "▲" : "▼"}</span>
            </div>

            {/* DROPDOWN */}

            {floorDropdownOpen && (
              <div style={styles.dropdownMenu}>
                {floors.length === 0 ? (
                  <div style={styles.noFloorOption}>No floors available</div>
                ) : (
                  floors.map((floor) => {
                    const floorAvailableBeds = Number(
                      floor.availableBedByFloorRooms || 0,
                    );

                    const isAvailable = floorAvailableBeds > 0;

                    const isSelected = selectedFloor === floor.floorId;

                    return (
                      <div
                        key={floor.floorId}
                        style={{
                          ...styles.dropdownOption,
                          ...(isSelected ? styles.dropdownOptionSelected : {}),
                        }}
                        onClick={() => handleFloorChange(floor.floorId)}
                      >
                        <div style={styles.optionLeft}>
                          <span style={styles.optionIcon}>🏢</span>

                          <span style={styles.optionFloorName}>
                            {floor.floorName}
                          </span>
                        </div>

                        <div style={styles.optionRight}>
                          <span style={styles.optionBedCount}>
                            {floorAvailableBeds} beds
                          </span>

                          <span
                            style={
                              isAvailable ? styles.greenBadge : styles.redBadge
                            }
                          >
                            {isAvailable ? "Available" : "Exceed"}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {/* =================================================
            FLOOR STATUS
        ================================================= */}

        {selectedFloorData && (
          <div
            style={{
              ...styles.floorStatus,
              ...(isBedCreationExceeded
                ? styles.floorStatusExceeded
                : styles.floorStatusAvailable),
            }}
          >
            <div style={styles.floorStatusTop}>
              <div>
                <div style={styles.floorStatusTitle}>
                  {selectedFloorData.floorName}
                </div>

                <div style={styles.floorStatusSubtitle}>
                  Current floor configuration
                </div>
              </div>

              <div
                style={
                  isBedCreationExceeded
                    ? styles.statusBadgeRed
                    : styles.statusBadgeGreen
                }
              >
                {isBedCreationExceeded ? "● LIMIT REACHED" : "● AVAILABLE"}
              </div>
            </div>

            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <span style={styles.statIcon}>🏢</span>

                <div>
                  <span style={styles.statLabel}>Total Rooms</span>

                  <strong style={styles.statValue}>
                    {selectedFloorData.totalRoomsCountByFloor || 0}
                  </strong>
                </div>
              </div>

              <div style={styles.statBox}>
                <span style={styles.statIcon}>🛏</span>

                <div>
                  <span style={styles.statLabel}>Available Beds</span>

                  <strong
                    style={{
                      ...styles.statValue,
                      color: isBedCreationExceeded ? "#dc2626" : "#15803d",
                    }}
                  >
                    {availableBeds}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =================================================
            ROOMS
        ================================================= */}

        <div style={styles.roomsHeader}>
          <div>
            <h2 style={styles.roomsTitle}>Room Configuration</h2>

            <p style={styles.roomsSubtitle}>
              {rooms.length > 0
                ? `${rooms.length} room${rooms.length > 1 ? "s" : ""} available`
                : "No rooms available"}
            </p>
          </div>
        </div>

        {rooms.length === 0 ? (
          <div style={styles.noRooms}>
            <div style={styles.noRoomsIcon}>🏢</div>

            <div style={styles.noRoomsTitle}>No Rooms Available</div>

            <div style={styles.noRoomsText}>
              There are no rooms configured for the selected floor.
            </div>
          </div>
        ) : (
          <div style={styles.grid}>
            {rooms.map((room) => {
              const createdBeds = Number(room.createdBeds || 0);

              const maxBeds = Number(room.maxBedsCount || 0);

              const roomAvailableBeds = Math.max(maxBeds - createdBeds, 0);

              const roomLimitReached = createdBeds >= maxBeds;

              const isLimitReached = isBedCreationExceeded || roomLimitReached;

              const percentage =
                maxBeds > 0 ? Math.min((createdBeds / maxBeds) * 100, 100) : 0;

              return (
                <div
                  key={room.roomId}
                  style={{
                    ...styles.roomCard,
                    ...(isLimitReached
                      ? styles.roomCardExceeded
                      : styles.roomCardAvailable),
                  }}
                >
                  {/* ROOM HEADER */}

                  <div style={styles.roomHeader}>
                    <div style={styles.roomIcon}>🚪</div>

                    <div style={styles.roomName}>{room.roomName}</div>
                  </div>

                  {/* BED COUNT */}

                  <div style={styles.bedCountSection}>
                    <div style={styles.bedCountTop}>
                      <span style={styles.bedCountLabel}>Beds</span>

                      <span
                        style={{
                          ...styles.bedCount,
                          color: isLimitReached ? "#dc2626" : "#2563eb",
                        }}
                      >
                        {createdBeds}
                        <span style={styles.bedMax}> / {maxBeds}</span>
                      </span>
                    </div>

                    {/* PROGRESS BAR */}

                    <div style={styles.progressBackground}>
                      <div
                        style={{
                          ...styles.progressBar,
                          width: `${percentage}%`,
                          background: isLimitReached ? "#ef4444" : "#2563eb",
                        }}
                      />
                    </div>
                  </div>

                  {/* AVAILABLE */}

                  <div
                    style={
                      isLimitReached
                        ? styles.availableBoxRed
                        : styles.availableBoxGreen
                    }
                  >
                    <span>Available Beds</span>

                    <strong>{roomAvailableBeds}</strong>
                  </div>

                  {/* WARNING */}

                  {roomLimitReached && (
                    <div style={styles.warning}>⚠ Bed limit reached</div>
                  )}

                  {!roomLimitReached && isBedCreationExceeded && (
                    <div style={styles.warning}>⚠ Floor bed limit reached</div>
                  )}

                  {/* CREATE BUTTON */}

                  <button
                    type="button"
                    disabled={isLimitReached}
                    style={{
                      ...styles.createButton,
                      ...(isLimitReached
                        ? styles.createButtonDisabled
                        : styles.createButtonActive),
                    }}
                    onClick={() => handleCreateBed(room)}
                  >
                    {isLimitReached
                      ? "🔒 Create Bed Disabled"
                      : "➕ Create Bed"}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* =================================================
            BOTTOM DASHBOARD BUTTON
        ================================================= */}

        <div style={styles.bottomSection}>
          <button
            type="button"
            style={styles.dashboardButton}
            onClick={handleDashboard}
          >
            ← Floor-room-Beds Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default FloorRoomBedSelector;

// =====================================================
// STYLES
// =====================================================

const styles = {
  // =====================================================
  // CONTAINER
  // =====================================================

  container: {
    minHeight: "100vh",
    width: "100%",
    background:
      "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eef2ff 100%)",
    padding: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    boxSizing: "border-box",
    fontFamily: "'Segoe UI', 'Poppins', Arial, sans-serif",
  },

  // =====================================================
  // MAIN CARD
  // =====================================================

  card: {
    width: "100%",
    maxWidth: "1100px",
    background: "#ffffff",
    borderRadius: "20px",
    padding: "26px",
    boxSizing: "border-box",
    boxShadow: "0 15px 45px rgba(15, 23, 42, 0.10)",
    border: "1px solid #e5e7eb",
  },

  // =====================================================
  // PAGE HEADER
  // =====================================================

  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px",
    paddingBottom: "22px",
    marginBottom: "22px",
    borderBottom: "1px solid #e5e7eb",
  },

  pageBadge: {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: "6px",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "10px",
    fontWeight: "800",
    letterSpacing: "1px",
    marginBottom: "7px",
  },

  title: {
    margin: 0,
    color: "#172554",
    fontSize: "27px",
    fontWeight: "800",
    lineHeight: 1.2,
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  totalFloors: {
    minWidth: "75px",
    padding: "10px 15px",
    borderRadius: "12px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    textAlign: "center",
  },

  totalNumber: {
    display: "block",
    color: "#1e3a8a",
    fontSize: "22px",
    fontWeight: "800",
    lineHeight: 1,
  },

  totalLabel: {
    display: "block",
    marginTop: "4px",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: "600",
  },

  // =====================================================
  // SECTION
  // =====================================================

  section: {
    marginBottom: "20px",
  },

  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "700",
    marginBottom: "8px",
  },

  // =====================================================
  // DROPDOWN
  // =====================================================

  dropdownContainer: {
    position: "relative",
    width: "100%",
  },

  dropdownHeader: {
    width: "100%",
    minHeight: "50px",
    padding: "9px 14px",
    borderRadius: "10px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    boxSizing: "border-box",
    transition: "all .2s ease",
  },

  dropdownHeaderActive: {
    border: "1px solid #2563eb",
    boxShadow: "0 0 0 3px rgba(37,99,235,.10)",
  },

  selectedFloorContent: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  selectedFloorName: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#1e293b",
  },

  placeholder: {
    color: "#94a3b8",
    fontSize: "14px",
  },

  arrow: {
    color: "#64748b",
    fontSize: "10px",
  },

  dropdownMenu: {
    position: "absolute",
    top: "57px",
    left: 0,
    right: 0,
    background: "#ffffff",
    border: "1px solid #dbe3ef",
    borderRadius: "10px",
    boxShadow: "0 15px 30px rgba(15,23,42,.15)",
    zIndex: 1000,
    overflow: "hidden",
  },

  dropdownOption: {
    minHeight: "52px",
    padding: "9px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
    boxSizing: "border-box",
    transition: "background .2s ease",
  },

  dropdownOptionSelected: {
    background: "#eff6ff",
  },

  optionLeft: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    minWidth: 0,
  },

  optionIcon: {
    fontSize: "16px",
  },

  optionFloorName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1e293b",
  },

  optionRight: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  optionBedCount: {
    fontSize: "12px",
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  noFloorOption: {
    padding: "18px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "13px",
  },

  // =====================================================
  // BADGES
  // =====================================================

  greenBadge: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "4px 8px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: "800",
    whiteSpace: "nowrap",
  },

  redBadge: {
    background: "#fee2e2",
    color: "#dc2626",
    padding: "4px 8px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: "800",
    whiteSpace: "nowrap",
  },

  // =====================================================
  // FLOOR STATUS
  // =====================================================

  floorStatus: {
    borderRadius: "14px",
    padding: "16px",
    marginBottom: "24px",
    border: "1px solid",
  },

  floorStatusAvailable: {
    background: "#f0fdf4",
    borderColor: "#bbf7d0",
  },

  floorStatusExceeded: {
    background: "#fef2f2",
    borderColor: "#fecaca",
  },

  floorStatusTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
    marginBottom: "14px",
  },

  floorStatusTitle: {
    color: "#1e293b",
    fontSize: "17px",
    fontWeight: "800",
  },

  floorStatusSubtitle: {
    marginTop: "3px",
    color: "#64748b",
    fontSize: "11px",
  },

  statusBadgeGreen: {
    background: "#dcfce7",
    color: "#15803d",
    padding: "6px 10px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: "800",
    whiteSpace: "nowrap",
  },

  statusBadgeRed: {
    background: "#fee2e2",
    color: "#dc2626",
    padding: "6px 10px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: "800",
    whiteSpace: "nowrap",
  },

  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },

  statBox: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    background: "rgba(255,255,255,.75)",
    borderRadius: "10px",
    border: "1px solid rgba(148,163,184,.20)",
  },

  statIcon: {
    fontSize: "19px",
  },

  statLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "600",
  },

  statValue: {
    display: "block",
    marginTop: "2px",
    color: "#1e3a8a",
    fontSize: "18px",
    fontWeight: "800",
  },

  // =====================================================
  // ROOMS HEADER
  // =====================================================

  roomsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "13px",
  },

  roomsTitle: {
    margin: 0,
    color: "#1e293b",
    fontSize: "18px",
    fontWeight: "800",
  },

  roomsSubtitle: {
    margin: "3px 0 0",
    color: "#94a3b8",
    fontSize: "11px",
  },

  // =====================================================
  // ROOM GRID
  // =====================================================

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
  },

  roomCard: {
    background: "#ffffff",
    borderRadius: "14px",
    padding: "16px",
    border: "1px solid",
    boxShadow: "0 5px 15px rgba(15,23,42,.06)",
    transition: "all .25s ease",
    boxSizing: "border-box",
  },

  roomCardAvailable: {
    borderColor: "#bbf7d0",
  },

  roomCardExceeded: {
    borderColor: "#fecaca",
  },

  // =====================================================
  // ROOM HEADER
  // =====================================================

  roomHeader: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    marginBottom: "14px",
  },

  roomIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "9px",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "17px",
  },

  roomName: {
    color: "#1e293b",
    fontSize: "15px",
    fontWeight: "800",
  },

  // =====================================================
  // BED COUNT
  // =====================================================

  bedCountSection: {
    marginBottom: "11px",
  },

  bedCountTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "6px",
  },

  bedCountLabel: {
    color: "#64748b",
    fontSize: "11px",
    fontWeight: "600",
  },

  bedCount: {
    fontSize: "18px",
    fontWeight: "800",
  },

  bedMax: {
    color: "#94a3b8",
    fontSize: "13px",
    fontWeight: "600",
  },

  progressBackground: {
    width: "100%",
    height: "6px",
    background: "#e2e8f0",
    borderRadius: "10px",
    overflow: "hidden",
  },

  progressBar: {
    height: "100%",
    borderRadius: "10px",
    transition: "width .3s ease",
  },

  // =====================================================
  // AVAILABLE BOX
  // =====================================================

  availableBoxGreen: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    marginBottom: "10px",
    borderRadius: "8px",
    background: "#f0fdf4",
    color: "#15803d",
    fontSize: "11px",
    fontWeight: "600",
  },

  availableBoxRed: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 10px",
    marginBottom: "10px",
    borderRadius: "8px",
    background: "#fef2f2",
    color: "#dc2626",
    fontSize: "11px",
    fontWeight: "600",
  },

  // =====================================================
  // WARNING
  // =====================================================

  warning: {
    color: "#dc2626",
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: "7px",
    padding: "6px 8px",
    marginBottom: "10px",
    fontSize: "10px",
    fontWeight: "700",
  },

  // =====================================================
  // CREATE BUTTON
  // =====================================================

  createButton: {
    width: "100%",
    padding: "9px 10px",
    border: "none",
    borderRadius: "8px",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "700",
    transition: "all .2s ease",
  },

  createButtonActive: {
    background: "#2563eb",
    cursor: "pointer",
  },

  createButtonDisabled: {
    background: "#cbd5e1",
    color: "#64748b",
    cursor: "not-allowed",
  },

  // =====================================================
  // NO ROOMS
  // =====================================================

  noRooms: {
    padding: "45px 20px",
    textAlign: "center",
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
    borderRadius: "14px",
  },

  noRoomsIcon: {
    fontSize: "32px",
    marginBottom: "8px",
  },

  noRoomsTitle: {
    color: "#334155",
    fontSize: "16px",
    fontWeight: "800",
  },

  noRoomsText: {
    marginTop: "4px",
    color: "#94a3b8",
    fontSize: "12px",
  },

  // =====================================================
  // BOTTOM DASHBOARD BUTTON
  // =====================================================

  bottomSection: {
    display: "flex",
    justifyContent: "flex-start",
    marginTop: "25px",
    paddingTop: "18px",
    borderTop: "1px solid #e5e7eb",
  },

  dashboardButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    padding: "10px 16px",
    border: "none",
    borderRadius: "9px",
    background: "#1e3a8a",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 5px 12px rgba(30,58,138,.20)",
    transition: "all .25s ease",
  },

  // =====================================================
  // POPUP
  // =====================================================

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#111827",
    color: "#ffffff",
    padding: "11px 16px",
    borderRadius: "9px",
    fontSize: "12px",
    fontWeight: "600",
    zIndex: 9999,
    boxShadow: "0 10px 25px rgba(0,0,0,.20)",
  },

  popupIcon: {
    color: "#fbbf24",
    fontWeight: "800",
  },
};
